import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import PropertyVisual from './PropertyVisual';
import { stayProperties } from '../pages/stayProperties';

gsap.registerPlugin(useGSAP);
export const properties = Object.values(stayProperties).map(property => ({ ...property, location: property.name.toUpperCase(), title: property.name, description: property.introduction, detail: property.overview, image: property.image ? `${process.env.PUBLIC_URL}/images/${property.image}` : '' }));
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default function PropertySlider() {
  const scope = useRef(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  // Shared mutable motion keeps both canvases synchronized without rendering React on every frame.
  const motion = useRef({ progress: 0, velocity: 0, direction: 1, dragging: false, listeners: new Set() });
  const gesture = useRef(null);
  const tween = useRef(null);
  const locked = useRef(false);
  const settleRef = useRef(null);
  const { contextSafe } = useGSAP({ scope });
  const notify = () => motion.current.listeners.forEach(wake => wake());

  const settle = contextSafe((commit, direction = 1, velocity = 0) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    motion.current.dragging = false;
    motion.current.direction = direction;
    motion.current.velocity = clamp(Math.abs(velocity), 0, 2.5);
    scope.current.classList.remove('is-dragging');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    tween.current = gsap.to(motion.current, {
      progress: commit ? 1 : 0,
      duration: reduced ? 0.15 : commit ? 1.05 - Math.min(Math.abs(velocity) * 0.1, 0.25) : 0.45,
      ease: commit ? 'power3.inOut' : 'power3.out',
      onUpdate: notify,
      onComplete: () => {
        // React changes the HTML content only after the outgoing image has left.
        if (commit) setActive(index => (index + (direction < 0 ? -1 : 1) + properties.length) % properties.length);
        motion.current.progress = 0;
        motion.current.velocity = 0;
        locked.current = false;
        setBusy(false);
        notify();
      },
    });
  });
  settleRef.current = settle;

  useEffect(() => {
    return () => tween.current?.kill();
  }, []);

  function pointerDown(event) {
    if (locked.current || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const card = event.target.closest('.property-card');
    if (!card || event.target.closest('a, button, input, select')) return;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, time: performance.now(), width: card.clientWidth, velocity: 0, started: false };
  }

  function pointerMove(event) {
    const drag = gesture.current;
    if (!drag) return;
    if (drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.started) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { gesture.current = null; return; }
      if (Math.abs(dx) < 8) return;
      drag.started = true;
      scope.current.setPointerCapture(event.pointerId);
      motion.current.dragging = true;
      scope.current.classList.add('is-dragging');
    }
    const now = performance.now();
    drag.velocity = clamp(0.45 * drag.velocity + 0.55 * (event.clientX - drag.lastX) / Math.max(now - drag.time, 8), -2.5, 2.5);
    drag.lastX = event.clientX;
    drag.time = now;
    motion.current.direction = dx < 0 ? 1 : -1;
    motion.current.progress = clamp(Math.abs(dx) / drag.width, 0, 0.96);
    motion.current.velocity = Math.abs(drag.velocity);
    notify();
  }

  function pointerUp(event, cancelled = false) {
    const drag = gesture.current;
    if (!drag || drag.id !== event.pointerId) return;
    gesture.current = null;
    if (scope.current.hasPointerCapture(event.pointerId)) scope.current.releasePointerCapture(event.pointerId);
    if (!drag.started) return;
    const velocity = performance.now() - drag.time > 100 ? 0 : drag.velocity;
    const progress = motion.current.progress;
    const projected = progress + (velocity * -motion.current.direction * 0.16);
    settle(!cancelled && (progress > 0.3 || projected > 0.22), motion.current.direction, velocity);
  }

  function keyDown(event) {
    if (event.target.closest('a')) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      settle(true, event.key === 'ArrowRight' ? 1 : -1);
    }
  }

  return (
    <div ref={scope} className="property-slider" role="region" aria-roledescription="carousel" aria-label="Georgia, Jamaica and Oregon stays" tabIndex={0}
      onKeyDown={keyDown} onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={event => pointerUp(event, true)} onLostPointerCapture={event => pointerUp(event, true)}>
      <div className="property-slider__cards">
        {[0, 1].map(slot => {
          const index = (active + slot) % properties.length;
          const property = properties[index];
          return (
            <div className="property-card-shell" key={slot}>
              <article className="property-card" aria-labelledby={`property-title-${slot}`}>
                {property.image ? <PropertyVisual properties={properties} index={index} motion={motion.current} /> : <div className="property-visual property-photo-pending">Property photographs to follow</div>}
                <div className="property-card__content">
                  <p className="destination-eyebrow">JamRock Escape</p>
                  <h3 id={`property-title-${slot}`}>{property.title}</h3>
                  <p className="property-card__description">{property.description}</p>
                  <p className="property-card__detail">{property.detail}</p>
                  <a className="property-card__button" href={property.path} aria-label={`View this stay: ${property.title}`}>{property.comingSoon ? 'Explore coming soon stay' : 'View this stay'} <span aria-hidden="true">↗</span></a>
                </div>
              </article>
            </div>
          );
        })}
      </div>
      <div className="property-slider__controls" aria-label="Destination controls">
        <button type="button" aria-label="Previous destination" onClick={() => settle(true, -1)} disabled={busy}><span aria-hidden="true">←</span></button>
        <span className="property-slider__position" aria-hidden="true">{active + 1} / {properties.length}</span>
        <button type="button" aria-label="Next destination" onClick={() => settle(true, 1)} disabled={busy}><span aria-hidden="true">→</span></button>
      </div>
      <p className="destination-sr-only" role="status" aria-live="polite" aria-atomic="true">Showing {properties[active].location.toLowerCase()} first. Use the previous and next destination buttons, arrow keys, or swipe to change destination.</p>
    </div>
  );
}
